import { addTo } from '../../../util/index.js';
import { claimable, openBeyond, payableUnder, roomByDay, } from './ceiling.js';
import { NEVER } from './dates.js';
import { settleDerived } from './derived.js';
import { isActive, isOpen, ledgerOpen, } from './ledger.js';
// The ledgers of covered interest, its derived interest booked.
const coveredLedgers = (caseState) => caseState.interestEntries
    .filter((entry) => entry.ceilingCovered)
    .map((entry) => {
    settleDerived(entry);
    return entry.ledger;
});
// D31: what of the covered interest can be claimed now; null when all of it.
export const claims = (caseState) => {
    if (caseState.eventClaims !== undefined) {
        return caseState.eventClaims;
    }
    const claimsByLedger = caseState.ceiling === null
        ? null
        : claimable(coveredLedgers(caseState), caseState.ceiling.budget);
    if (caseState.inEvent) {
        caseState.eventClaims = claimsByLedger;
    }
    return claimsByLedger;
};
// The statement shows covered interest as it becomes claimable, so it notes
// where that stood at each step.
export const noteClaimSteps = (caseState, from, to) => {
    const claimsByLedger = claims(caseState);
    for (const entry of caseState.interestEntries) {
        if (entry.ceilingCovered) {
            entry.interest.claimSteps.push({
                from,
                to,
                boundary: claimsByLedger?.get(entry.ledger)?.boundary ?? NEVER,
            });
        }
    }
};
export const claimableOpen = (ledger, claim) => {
    if (claim === undefined) {
        return ledgerOpen(ledger);
    }
    const payable = payableUnder(claim);
    let amount = 0;
    for (const tranche of ledger.tranches) {
        if (isOpen(tranche)) {
            amount += payable(tranche);
        }
    }
    return amount;
};
export const claimOf = (entry) => entry.kind === 'interest' && entry.ceilingCovered
    ? claims(entry.caseState)?.get(entry.ledger)
    : undefined;
export const snapshotClaims = (caseState) => {
    const claimsByLedger = claims(caseState);
    if (claimsByLedger === null) {
        return null;
    }
    return caseState.interestEntries
        .filter((entry) => entry.ceilingCovered)
        .map((entry) => {
        const ledger = entry.ledger;
        const claim = claimsByLedger.get(ledger);
        // Reinstated interest activated later the same day was not claimable.
        const streams = new Set(ledger.tranches.filter(isActive).map((tranche) => tranche.stream));
        return {
            entry,
            claim,
            streams,
            before: openBeyond(ledger, claim, streams),
            counted: 0,
        };
    });
};
export const noteUnclaimed = (snapshot, reason) => {
    for (const item of snapshot ?? []) {
        settleDerived(item.entry);
        const now = openBeyond(item.entry.ledger, item.claim, item.streams);
        let unclaimed = item.before.after - now.after;
        for (const [stream, open] of item.before.onBoundary) {
            unclaimed +=
                open -
                    Math.max(now.onBoundary.get(stream) ?? 0, item.claim.exactShares.get(stream) ?? 0);
        }
        item.entry.unclaimed[reason] += unclaimed - item.counted;
        item.counted = unclaimed;
    }
};
// Interest accruing on a closed voucher is written off as it accrues. Each
// day of it counts as far as it was claimable: after the open cost of older
// days, sharing what is left pro rata with the open cost of its own day.
// Judged at the next stop, as nothing in between changes those days.
export const noteClosedAccruals = (caseState) => {
    const accruals = caseState.closedAccruals;
    caseState.closedAccruals = [];
    if (accruals.length === 0 || caseState.ceiling === null) {
        return;
    }
    const closedByDay = new Map();
    for (const accrual of accruals) {
        for (let day = accrual.from; day < accrual.to; day++) {
            addTo(closedByDay, day, accrual.perDay);
        }
    }
    const rooms = roomByDay(coveredLedgers(caseState), caseState.ceiling.budget, [
        ...closedByDay.keys(),
    ]);
    for (const { entry, from, to, perDay } of accruals) {
        for (let day = from; day < to; day++) {
            const { room, open } = rooms.get(day);
            const total = open + closedByDay.get(day);
            const claimed = total <= room ? perDay : (perDay * room) / total;
            entry.unclaimed[entry.ledger.closed] += perDay - claimed;
        }
    }
};
//# sourceMappingURL=claims.js.map